import { Button } from "@/components/primitives/button";
import { Tooltip } from "@/components/primitives/tooltip";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { applyMarkdownFormat } from "@/lib/markdown";
import { useCallback, useState, useRef } from "react";
import {
  TOOLBAR_PRIMARY,
  TOOLBAR_OVERFLOW,
  type FormattingOption,
} from "@/config/formattingConfig";
import type { MarkdownFormat } from "@/lib/markdown";
import { Check, ArrowLeft, Ellipsis } from "lucide-react";

interface MarkdownToolbarProps {
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  value: string;
  onChange: (newValue: string) => void;
}

export default function MarkdownToolbar({
  textareaRef,
  value,
  onChange,
}: MarkdownToolbarProps) {
  const { t } = useTranslation();
  const [lastApplied, setLastApplied] = useState<MarkdownFormat | null>(null);
  const [showOverflow, setShowOverflow] = useState(false);
  const [isLinkOpen, setIsLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const linkInputRef = useRef<HTMLInputElement>(null);
  const savedSelectionRef = useRef({ start: 0, end: 0 });

  // Keep a ref to avoid stale closures over `value` and `onChange`
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const applyFormat = useCallback(
    (format: MarkdownFormat) => {
      const textarea = textareaRef.current;
      if (!textarea) {
        return;
      }

      if (format === "link") {
        savedSelectionRef.current = {
          start: textarea.selectionStart,
          end: textarea.selectionEnd,
        };
        setIsLinkOpen(true);
        setTimeout(() => linkInputRef.current?.focus(), 0);
        return;
      }

      const { selectionStart, selectionEnd } = textarea;
      const result = applyMarkdownFormat(
        valueRef.current,
        selectionStart,
        selectionEnd,
        format,
      );

      onChangeRef.current(result.newText);
      setLastApplied(format);

      setTimeout(() => setLastApplied(null), 200);

      requestAnimationFrame(() => {
        textarea.focus();
        textarea.setSelectionRange(
          result.newSelectionStart,
          result.newSelectionEnd,
        );
      });
    },
    [textareaRef],
  );

  const handleLinkConfirm = useCallback(() => {
    const url = linkUrl.trim();
    if (!url) {
      return;
    }
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }

    const { start, end } = savedSelectionRef.current;
    const current = valueRef.current;
    const before = current.slice(0, start);
    const selected = current.slice(start, end);
    const after = current.slice(end);
    const linkText = selected || "link";
    const newText = `${before}[${linkText}](${url})${after}`;
    const newEnd = start + `[${linkText}](${url})`.length;

    onChangeRef.current(newText);
    setIsLinkOpen(false);
    setLinkUrl("");
    setLastApplied("link");
    setTimeout(() => setLastApplied(null), 200);

    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(newEnd, newEnd);
    });
  }, [linkUrl, textareaRef]);

  const handleLinkBack = useCallback(() => {
    setIsLinkOpen(false);
    setLinkUrl("");
    textareaRef.current?.focus();
  }, [textareaRef]);

  const renderButton = (opt: FormattingOption) => {
    const Icon = opt.icon;
    return (
      <Tooltip key={opt.key} content={t(opt.labelKey)}>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className={cn(
            "size-7 rounded-none hover:bg-accent hover:text-accent-foreground",
            lastApplied === opt.key && "bg-accent text-accent-foreground",
          )}
          aria-label={t(opt.labelKey)}
          onClick={() => applyFormat(opt.key)}
        >
          <Icon size={16} />
        </Button>
      </Tooltip>
    );
  };

  const visibleFormats = showOverflow ? TOOLBAR_OVERFLOW : TOOLBAR_PRIMARY;

  return (
    <div className="relative flex items-center gap-2 py-1">
      <div
        className={cn(
          "flex items-center transition-opacity duration-150",
          isLinkOpen && "pointer-events-none opacity-0",
        )}
      >
        <div className="flex items-center divide-x divide-border overflow-hidden rounded-md border border-border">
          {showOverflow && (
            <Tooltip content={t("editor.formattingOptions")}>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="size-7 rounded-none hover:bg-accent hover:text-accent-foreground"
                aria-label={t("editor.formattingOptions")}
                onClick={() => setShowOverflow(false)}
              >
                <ArrowLeft size={16} />
              </Button>
            </Tooltip>
          )}
          {visibleFormats.map(renderButton)}
          {!showOverflow && (
            <Tooltip content={t("editor.more")}>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="size-7 rounded-none hover:bg-accent hover:text-accent-foreground"
                aria-label={t("editor.more")}
                onClick={() => setShowOverflow(true)}
              >
                <Ellipsis size={16} />
              </Button>
            </Tooltip>
          )}
        </div>
      </div>

      {isLinkOpen && (
        <div
          className="absolute inset-0 flex items-center gap-1"
          onMouseDown={(e) => {
            if ((e.target as HTMLElement).tagName !== "INPUT") {
              e.preventDefault();
            }
          }}
        >
          <Tooltip content={t("editor.formattingOptions")}>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="size-7 shrink-0 hover:bg-accent hover:text-accent-foreground"
              aria-label={t("editor.formattingOptions")}
              onClick={handleLinkBack}
            >
              <ArrowLeft size={16} />
            </Button>
          </Tooltip>
          <div className="mx-0.5 h-5 w-px shrink-0 bg-border" />
          <input
            ref={linkInputRef}
            className="min-w-[120px] flex-1 bg-transparent px-2 py-1 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
            type="text"
            value={linkUrl}
            placeholder={t("editor.link")}
            autoComplete="off"
            inputMode="url"
            dir="auto"
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && linkUrl.trim()) {
                e.preventDefault();
                handleLinkConfirm();
              }
              if (e.key === "Escape") {
                handleLinkBack();
              }
            }}
          />
          {linkUrl.trim() && (
            <>
              <div className="mx-0.5 h-5 w-px shrink-0 bg-border" />
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="size-7 shrink-0 text-primary hover:bg-accent hover:text-accent-foreground"
                aria-label={t("editor.link")}
                onClick={handleLinkConfirm}
              >
                <Check size={16} />
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
